import {
  BrowserPageNavigationCompletedEventData,
  DefaultWebViewOptions,
  InAppBrowser,
} from '@capacitor/inappbrowser';
import { CapacitorStatusBarInitialization } from 'modules/app';
import errorMessages from 'modules/authentication/components/AuthErrors';
import { fetchAccountInformation } from 'modules/authentication/helpers/account';
import {
  AUTH_DOMAINS,
  SOCIAL_PROVIDERS,
} from 'modules/authentication/helpers/constants';
import { clearAuthCookies } from 'modules/authentication/helpers/cookies';
import {
  SocialProvider,
  isNativeAuthAvailable,
  signInWithNative,
} from 'modules/authentication/helpers/nativeSocialAuth';
import { isCordova } from 'utils/platform';
import useAuthStore from '~/authentication/store';

declare const window: Window & { location: Location };

export const authenticateWithNativeToken = async (
  provider: SocialProvider,
  idToken?: string,
  accessToken?: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const body = JSON.stringify({
      idToken,
      accessToken,
      provider,
    });

    const response = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/v1/signin/native`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Authentication failed: ${response.status}`,
      );
    }

    const data = await response.json();

    if (!data.token) {
      return { success: false, error: 'No token received from server' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('[authenticateWithNativeToken] Error:', error);
    return { success: false, error: error.message || 'Authentication failed' };
  }
};

const tryNativeSignIn = async (
  provider: SocialProvider,
  callbackUrl: string,
): Promise<
  | { handled: true; success: boolean; redirectUrl?: string; error?: string }
  | { handled: false }
> => {
  if (!isNativeAuthAvailable(provider)) {
    return { handled: false };
  }

  console.log(`[signIn] Attempting native ${provider} sign-in`);
  const nativeResult = await signInWithNative(provider);

  if (nativeResult.cancelled) {
    return { handled: true, success: false, error: 'User cancelled sign-in' };
  }

  if (
    !nativeResult.success ||
    (!nativeResult.idToken && !nativeResult.accessToken)
  ) {
    console.warn(
      `[signIn] Native ${provider} sign-in failed, falling back to web flow:`,
      nativeResult.error,
    );
    return { handled: false };
  }

  const authResult = await authenticateWithNativeToken(
    provider,
    nativeResult.idToken,
    nativeResult.accessToken,
  );

  if (!authResult.success) {
    console.warn(
      `[signIn] Native token auth failed for ${provider}, falling back to web flow:`,
      authResult.error,
    );
    return { handled: false };
  }

  console.log(`[signIn] Native ${provider} sign-in successful`);
  return { handled: true, success: true, redirectUrl: callbackUrl };
};

export const signIn = async (
  provider: string,
  callbackUrl = '/',
  onFinish: ({
    error,
    success,
    redirectUrl,
  }: {
    error?: any;
    success: boolean;
    redirectUrl?: string;
  }) => void,
): Promise<void> => {
  try {
    await clearAuthCookies();
    if (SOCIAL_PROVIDERS.includes(provider as SocialProvider)) {
      const nativeAttempt = await tryNativeSignIn(
        provider as SocialProvider,
        callbackUrl,
      );

      if (nativeAttempt.handled) {
        void fetchAccountInformation(useAuthStore.getState(), {
          silent: false,
        });
        onFinish({
          success: nativeAttempt.success,
          redirectUrl: nativeAttempt.redirectUrl,
          error: nativeAttempt.error,
        });
        return;
      }
    }

    // Web-based OAuth flow
    const params = new URLSearchParams();
    params.set('callbackUrl', callbackUrl ?? '/');
    params.set('json', 'true');

    const signinResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/auth/signin/${provider}`,
      {
        method: 'POST',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: params.toString(),
      },
    );

    const signin = await signinResponse.json();

    const { url } = signin;
    if (isCordova()) {
      await InAppBrowser.openInWebView({
        url,
        options: {
          ...DefaultWebViewOptions,
          showToolbar: true,
          showNavigationButtons: true,
          showURL: false,
          clearCache: false,
          clearSessionCache: false,
          customWebViewUserAgent: 'Mozilla/5.0 Google',
          iOS: {
            ...DefaultWebViewOptions.iOS,
            surpressIncrementalRendering: false,
          },
        },
      });

      await InAppBrowser.addListener(
        'browserPageNavigationCompleted',
        async (event: BrowserPageNavigationCompletedEventData) => {
          if (AUTH_DOMAINS.some((host) => event.url.startsWith(host))) {
            void fetchAccountInformation(useAuthStore.getState(), {
              silent: false,
            });
            await InAppBrowser.close();

            if (!event.url.includes('api/auth/callback')) {
              onFinish({ success: true, redirectUrl: callbackUrl });
            }
          }
        },
      );
      await InAppBrowser.addListener('browserClosed', () => {
        CapacitorStatusBarInitialization();
      });
    } else {
      onFinish({ success: true, redirectUrl: url });
    }
  } catch (error) {
    onFinish({ success: false, error });
    console.error('[signIn] Error signing in', error, provider, callbackUrl);
  }
};

export const signInWithCredentials = async (
  username: string,
  password: string,
  callbackUrl: string,
): Promise<void> => {
  try {
    const params = new URLSearchParams();
    params.set('username', username);
    params.set('password', password);
    params.set('callbackUrl', callbackUrl);
    params.set('json', 'true');
    params.set('redirect', 'false');

    const signinResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/auth/callback/credentials`,
      {
        method: 'POST',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: params.toString(),
      },
    );

    const signin = await signinResponse.json();

    if (!signinResponse.ok) {
      const url = new URL(signin.url);

      const errorSearchParams = new URLSearchParams(url.search);
      throw new Error(errorMessages.getString(errorSearchParams.get('error')));
    }

    await fetchAccountInformation(useAuthStore.getState());

    if (isCordova()) {
      window.location.href = callbackUrl;
    } else {
      console.log('signin response', signin);
      const { url } = signin;

      window.location.href = url;
    }
  } catch (exception) {
    console.error(
      '[signInWithCredentials] Error signing in',
      exception,
      callbackUrl,
    );
    throw exception;
  }
};

export const signUpWithCredentials = async ({
  firstName,
  lastName,
  email,
  password,
  gender,
  phoneNumber,
  callbackUrl,
}: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  gender?: string;
  phoneNumber?: string;
  callbackUrl?: string;
}): Promise<void> => {
  const body = {
    firstName,
    email,
    password,
    lastName,
    gender,
    phoneNumber,
  };

  const signupResponse = await fetch(
    `${URL_AUTHENTICATION_HOST}/api/v1/auth/signup/`,
    {
      method: 'POST',
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  if (!signupResponse.ok) {
    const signup = await signupResponse.json();
    throw new Error(signup.error);
  }

  await signInWithCredentials(email, password, callbackUrl);
};
